import { useState } from 'react';
import {
    CARD_NUMBER_LENGTH,
    getInitialState,
    STEP_DATA,
    STEP_LIMIT,
    STEP_CONFIRM,
    STEP_CONFIRMATION,
    STEP_FAILED,
} from './activateCard.constants';
import {
    validateCardNumber,
    validateConfirmCardNumber,
    validateSpendingLimit,
    validateOnlyNumbers,
    formatCurrencyInput,
    validateFullName,
    hasDisplayFullName,
} from './activateCard.validators';
import { ActivateCardState } from './activateCard.types';
import { useActivateCard } from '@/features/card/hooks/useCard/useActivateCard';
import { useAuthContext } from '@/features/auth/context/AuthContext/AuthContext';
import { customerService } from '@/features/card/services/customerService';
import { mfaService } from '@/features/auth/services/mfaService';
import { isAdmin, isBasic } from '@/shared/config/permissions';
import { useEffect } from 'react';
import { useSpinner } from '@chikara/ui';
import { onboardingService } from '@/features/onboarding/services/onboardingService';
import { AccountCreateRequest, CardActivateRequest } from '@/features/card/types/card.types';
import { useAsyncGuard } from '@chikara/hooks';
import { t } from '@/i18n';

export const useActivateCardForm = (
    onClose: () => void,
    isOpen: boolean,
    onSuccess?: () => void
) => {
    const { userData, payload: authPayload } = useAuthContext();
    const [state, setState] = useState<ActivateCardState>({
        ...getInitialState(userData.fullName || '', userData.email || ''),
    });
    const [isMfaOpen, setIsMfaOpen] = useState(false);
    const [mfaToken, setMfaToken] = useState<string | null>(null);

    const { accountCreate, cardActivate } = useActivateCard();
    const { show, hide, isSpinnerLoading: isGlobalLoading } = useSpinner();
    const { checkGuard: activateGuard } = useAsyncGuard();

    useEffect(() => {
        const fetchSummary = async () => {
            if (isOpen && authPayload?.customer_uuid && authPayload?.user_uuid) {
                show();
                setState(prev => ({ ...prev, isLoading: true }));
                try {
                    const accountDetail = await onboardingService.accountGet(authPayload.user_uuid);
                    const info = accountDetail.data.data;
                    const accounts = info.accounts || [];

                    const withAccount = info.account_count === 1 && !accounts[0]?.card?.uuid && accounts[0]?.account_type === 'LINKED_ACCOUNT';


                    const newCard = accounts.length === 0;

                    if (withAccount) {
                        setState(prev => ({
                            ...prev,
                            step: STEP_DATA,
                            account_uuid: accounts[0].uuid,
                            withAccount: true,
                        }));
                    } else if (newCard) {
                        setState(prev => ({
                            ...prev,
                            newCard: true,
                        }));
                    }

                    const response = await customerService.getCustomerSummary();
                    setState(prev => ({
                        ...prev,
                        maxAvailableLimit: response.credit_line.total_available_to_assign,
                    }));
                } catch (error) {
                    console.error('Error fetching customer summary:', error);
                } finally {
                    hide();
                    setState(prev => ({ ...prev, isLoading: false }));
                }
            }
        };

        fetchSummary();
    }, [authPayload?.customer_uuid, show, hide, isOpen]);

    /* ---------------- Full Name ---------------- */

    const handleFullNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const error = validateFullName(value);
        setState(prev => ({
            ...prev,
            fullName: value,
            errors: { ...prev.errors, fullName: error },
        }));
    };

    /* ---------------- Card Number ---------------- */

    const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = validateOnlyNumbers(e.target.value);
        const error = validateCardNumber(value);

        setState(prev => {
            const confirmError = prev.confirmCardNumber
                ? validateConfirmCardNumber(prev.confirmCardNumber, value)
                : prev.errors.confirmCardNumber;

            return {
                ...prev,
                cardNumber: value,
                errors: {
                    ...prev.errors,
                    cardNumber: error,
                    confirmCardNumber: confirmError,
                },
            };
        });
    };

    const handleConfirmCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = validateOnlyNumbers(e.target.value);
        const error = validateConfirmCardNumber(value, state.cardNumber);

        setState(prev => ({
            ...prev,
            confirmCardNumber: value,
            errors: { ...prev.errors, confirmCardNumber: error },
        }));
    };

    /* ---------------- Spending Limit ---------------- */

    const handleSpendingLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, '');
        const formatted = formatCurrencyInput(e.target.value);
        let error = validateSpendingLimit(e.target.value);

        const numericLimit = Number(value) / 100;
        if (!error && numericLimit > state.maxAvailableLimit) {
            error = `El monto no puede ser mayor al disponible ($${state.maxAvailableLimit.toLocaleString('es-MX', { minimumFractionDigits: 2 })})`;
        }

        setState(prev => ({
            ...prev,
            spendingLimit: value,
            spendingLimitDisplay: formatted,
            errors: { ...prev.errors, spendingLimit: error },
        }));
    };

    const handleSpendingLimitFocus = () => {
        setState(prev => ({
            ...prev,
            isEditingLimit: true,
            spendingLimitDisplay: state.spendingLimit,
        }));
    };

    const handleSpendingLimitBlur = () => {
        setState(prev => ({
            ...prev,
            isEditingLimit: false,
            spendingLimitDisplay: formatCurrencyInput(prev.spendingLimit),
        }));
    };

    /* ---------------- Navigation ---------------- */
    // Check if step 1 is valid
    const canGoNext =
        (!hasDisplayFullName(state.fullName) || !validateFullName(state.fullName)) &&
        !validateCardNumber(state.cardNumber) &&
        !validateConfirmCardNumber(state.confirmCardNumber, state.cardNumber) &&
        state.cardNumber.length === CARD_NUMBER_LENGTH;

    // Check if step 2 is valid
    const canActivate =
        !validateSpendingLimit(state.spendingLimit) &&
        state.spendingLimit.length > 0 &&
        Number(state.spendingLimit) / 100 <= state.maxAvailableLimit;

    // Handle next button
    const handleNext = () => {
        setState(prev => {
            if (prev.step === STEP_DATA) {
                const nameError = hasDisplayFullName(prev.fullName)
                    ? validateFullName(prev.fullName)
                    : undefined;
                const cardError = validateCardNumber(prev.cardNumber);
                const confirmError = validateConfirmCardNumber(prev.confirmCardNumber, prev.cardNumber);

                const hasError = nameError || cardError || confirmError;

                if (!hasError && (isBasic(userData.role) || prev.withAccount)) {
                    handleActivate();
                    return {
                        ...prev,
                        errors: {
                            ...prev.errors,
                            fullName: nameError,
                            cardNumber: cardError,
                            confirmCardNumber: confirmError,
                        },
                    };
                }

                return {
                    ...prev,
                    errors: {
                        ...prev.errors,
                        fullName: nameError,
                        cardNumber: cardError,
                        confirmCardNumber: confirmError,
                    },
                    step: !nameError && !cardError && !confirmError ? STEP_LIMIT : prev.step,
                };
            }

            if (prev.step === STEP_LIMIT) {
                const limitError = validateSpendingLimit(prev.spendingLimit);
                const numericLimit = Number(prev.spendingLimit) / 100;
                const overLimitError =
                    numericLimit > prev.maxAvailableLimit
                        ? `El monto no puede ser mayor al disponible ($${prev.maxAvailableLimit.toLocaleString('es-MX', { minimumFractionDigits: 2 })})`
                        : undefined;

                const finalError = limitError || overLimitError;

                return {
                    ...prev,
                    errors: { ...prev.errors, spendingLimit: finalError },
                    step: finalError ? prev.step : STEP_CONFIRM,
                };
            }

            return prev;
        });
    };

    // Volver del resumen de confirmación al paso de límite
    const handleBackToLimit = () => {
        setState(prev => ({
            ...prev,
            step: STEP_LIMIT,
            errors: { ...prev.errors, spendingLimit: undefined },
        }));
    };

    // Open MFA Modal
    const openMfa = async () => {
        show();
        try {
            const { data } = await mfaService.mfaSend({
                command: 'card-activate',
                type: 'card-activate',
            });
            setMfaToken(data.mfa_token);
            setIsMfaOpen(true);
        } catch (error) {
            setState(prev => ({
                ...prev,
                errors: { ...prev.errors, spendingLimit: t('mfaError.send') },
            }));
        } finally {
            hide();
        }
    };

    // Handle MFA Verify
    const handleVerifyMfa = async (otp: string) => {
        if (!mfaToken) return;
        show();
        try {
            await mfaService.mfaVerify(otp, {
                mfa_token: mfaToken,
                command: 'card-activate',
                type: 'card-activate',
            });

            setIsMfaOpen(false);
            await handleActivate();
        } catch (error) {
            console.error('MFA Verify error:', error);
            setState(prev => ({
                ...prev,
                errors: { ...prev.errors, spendingLimit: t('mfaError.code') },
            }));
        } finally {
            hide();
        }
    };

    // Handle activate card
    const handleActivate = async () => {
        if (!activateGuard()) return;
        const limitError = validateSpendingLimit(state.spendingLimit);

        // ✅ Para BASIC o withAccount no validamos límite
        if (!isBasic(userData.role) && !state.withAccount && limitError) {
            setState(prev => ({
                ...prev,
                errors: { ...prev.errors, spendingLimit: limitError },
            }));
            return;
        }

        if (!authPayload?.user_uuid || !authPayload?.customer_uuid) {
            setState(prev => ({
                ...prev,
                errors: { ...prev.errors, fullName: 'Error al obtener datos del usuario' },
            }));
            return;
        }

        let success = false;

        if (state.withAccount && state.account_uuid) {
            const payloadCardActivate: CardActivateRequest = {
                card_number: state.cardNumber.replace(/\s/g, ''),
                account: state.account_uuid,
                card_in_hand: true,
            };
            success = await cardActivate(payloadCardActivate);
        } else {
            const payloadAccountCreate: AccountCreateRequest = {
                user_uuid: authPayload.user_uuid,
                customer_uuid: authPayload.customer_uuid,
                assigned_limit: isBasic(userData.role) ? 0 : Number(state.spendingLimit) / 100,
                role: userData.role as string,
                status: isAdmin(userData.role) ? 'enabled' : 'pending_approval',
                user: {
                    uuid: authPayload.user_uuid,
                    fullname: state.fullName,
                    email: state.email,
                },
                card_number: state.cardNumber.replace(/\s/g, ''),
            };
            success = await accountCreate(payloadAccountCreate);
        }

        if (success) {
            setState(prev => ({
                ...prev,
                step: STEP_CONFIRMATION,
                isPendingApproval: isBasic(userData.role) && !state.withAccount,
                errorCount: 0,
            }));
        } else {
            setState(prev => {
                const nextErrorCount = prev.errorCount + 1;
                if (nextErrorCount >= 3) {
                    return {
                        ...prev,
                        step: STEP_FAILED,
                        errorCount: nextErrorCount,
                    };
                }
                return {
                    ...prev,
                    step: STEP_DATA,
                    errorCount: nextErrorCount,
                };
            });
        }
    };

    // Handle success
    const handleSuccess = () => {
        onSuccess?.();
        setState(getInitialState(userData.fullName || '', userData.email || ''));
    };

    // Handle close
    const handleClose = () => {
        onClose();
        setState(getInitialState(userData.fullName || '', userData.email || ''));
    };

    return {
        state,
        handleCardNumberChange,
        handleConfirmCardNumberChange,
        handleSpendingLimitChange,
        handleSpendingLimitFocus,
        handleSpendingLimitBlur,
        handleNext,
        handleActivate,
        handleVerifyMfa,
        canGoNext,
        canActivate,
        handleClose,
        handleSuccess,
        setState,
        handleFullNameChange,
        isMfaOpen,
        setIsMfaOpen,
        openMfa,
        isGlobalLoading,
        handleBackToLimit,
    };
};
